import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import ProductFAQ from '@/components/landing/ProductFAQ';
import React, { Suspense } from 'react';
import Header from '@/components/landing/product/sub/Header'
import databg from '@/assets/products/econ.png';
import { Database, Zap, Globe, Clock, TrendingDown, Activity } from 'lucide-react';
import StyledProducts from '../styles/ProductsPageStyles';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

import Marquee from '@/components/landing/product/sub/Logos';
import FeaturesGrid from '@/components/landing/product/sub/FeaturesGrid';

const loadGlobeComp = () => import('@/components/landing/product/sub/Globe');
const GlobeComp = React.lazy(loadGlobeComp);
const loadDataGrid = () => import('@/components/landing/product/DataGrid');
const DataGrid = React.lazy(loadDataGrid);
const loadCardList = () => import('@/components/landing/product/CardList');
const CardList = React.lazy(loadCardList);
const loadResponses = () => import('@/components/landing/product/sub/Responses');
const Responses = React.lazy(loadResponses);
const loadDatasetDetails = () => import('@/components/landing/product/main/DatasetDetails');
const DatasetDetails = React.lazy(loadDatasetDetails);
const loadW1 = () => import('@/components/landing/product/main/datasets/macro');
const W1 = React.lazy(loadW1);
const loadW2 = () => import('@/components/landing/product/main/datasets/macro-nowcast-card');
const W2 = React.lazy(loadW2);
const loadW3 = () => import('@/components/landing/product/main/datasets/macro-regime-card');
const W3 = React.lazy(loadW3);
const loadW4 = () => import('@/components/landing/product/main/datasets/market-driver-waterfall-card');
const W4 = React.lazy(loadW4);
const loadW5 = () => import('@/components/landing/product/main/datasets/financial-quality-card');
const W5 = React.lazy(loadW5);
const loadOutcomes = () => import('@/components/landing/product/sub/OutcomeStats');
const Outcomes = React.lazy(loadOutcomes);
const loadPricingTable = () => import('@/components/landing/PricingTable');
const PricingTable = React.lazy(loadPricingTable);
const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);


const MacroEconomicsPage: React.FC = () => {
  useIdlePrefetch([loadGlobeComp, loadDataGrid, loadCardList, loadResponses, loadDatasetDetails, loadW1, loadW2, loadW3, loadW4, loadW5, loadOutcomes, loadPricingTable, loadGetStarted, loadFooter]);
  return (
    <>
      <SEOMetadata
        title="Macroeconomic Data API – Global Economic Indicators"
        description="Macroeconomic data API with 2M+ indicators across 195+ countries: GDP, inflation, employment, trade and central bank policy. Clean, normalized, research-ready."
        canonicalUrl="https://axionquant.com/products/economics"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "Axion Macroeconomic Data API",
          "description": "Macroeconomic data API with 2M+ economic indicators spanning GDP, inflation, employment, trade, and monetary policy for 195+ countries. Clean, normalized, and aligned for cross-country comparison and forecasting.",
          "brand": { "@type": "Brand", "name": "AxionQuant" },
          "image": "https://axionquant.com/meta.jpg",
          "category": "Macroeconomic Data API",
          "offers": { "@type": "AggregateOffer", "lowPrice": "29", "highPrice": "99", "priceCurrency": "USD", "availability": "https://schema.org/InStock", "description": "Free tier available; paid plans from $29/month" }
        })}</script>
      </Helmet>

      <StyledProducts>

      <Header
          docs="/docs/04-macros-and-signals"
        title="Macroeconomic Data API: Global Economic Indicators"
        body="2M+ economic indicators spanning GDP, inflation, employment, trade, and monetary policy for 195+ countries. Clean, normalized, and aligned for cross-country comparison and forecasting."
        crumb={{name: 'Economics', path:'/products/economics'}}
        tabs={
        [
          {
            label: 'GDP Growth',
            language: 'python',
            filename: 'gdp_growth.py',
            bg: '12.png',
            code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

# Real GDP series for two economies
us_id = client.econ.search("United States real GDP")[0]["id"]
de_id = client.econ.search("Germany real GDP")[0]["id"]

us = utils.df(client.econ.dataset(us_id))
de = utils.df(client.econ.dataset(de_id))

# Convert levels to growth rates
us = utils.relativity(us, cols=["value"])
de = utils.relativity(de, cols=["value"])

# Align both series on date for comparison
growth = utils.snap(
    [us[["date", "relative_value"]], de[["date", "relative_value"]]],
    names=["US", "Germany"],
    overwrite=["relative_value"],
    col="date"
)

visualize.polls(growth)`,
          },
          {
            label: 'Inflation Analysis',
            language: 'python',
            filename: 'cpi_inflation.py',
            bg: '13.png',
            code: `from axion import Axion, utils, models, visualize

client = Axion(api_key="your_api_key")

cpi_id = client.econ.search("CPI all items")[0]["id"]
cpi = utils.df(client.econ.dataset(cpi_id))

# Convert price levels to a monthly inflation rate
cpi = utils.relativity(cpi, cols=["value"])

# Project inflation six months forward
forecast = models.linearRegression(
    cpi,
    x="date",
    target="relative_value",
    n_preds=6,
    scale="M"
)

visualize.line(cpi, x="date", y="relative_value")`,
          },
          {
            label: 'Monetary Policy',
            language: 'python',
            filename: 'interest_rates.py',
            bg: '14.png',
            code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

# Historical policy rate
rate_id = client.econ.search("federal funds rate")[0]["id"]
rates = utils.df(client.econ.dataset(rate_id))

# Upcoming high-impact rate decisions
decisions = client.econ.calendar(
    country="US",
    category="interest rate",
    min_importance=3
)

visualize.line(rates, x="date", y="value")`,
          },
          {
            label: 'Trade Data',
            language: 'python',
            filename: 'trade_balance.py',
            bg: '11.png',
            code: `from axion import Axion, utils, visualize

client = Axion(api_key="your_api_key")

# Goods exports and imports for one economy
exports_id = client.econ.search("United States exports of goods")[0]["id"]
imports_id = client.econ.search("United States imports of goods")[0]["id"]

exports = utils.df(client.econ.dataset(exports_id))
imports = utils.df(client.econ.dataset(imports_id))

# Align both series and derive the trade balance
balance = utils.snap(
    [exports[["date", "value"]], imports[["date", "value"]]],
    names=["exports", "imports"],
    overwrite=["value"],
    col="date"
)

balance["net"] = balance["exports"] - balance["imports_value"]

visualize.line(balance, x="date", y="net")`,
          }]
      } />
      <Marquee/>

      <FeaturesGrid
        title="Macroeconomic Data API. "
        subtitle="Economic data, global indicators, and macro analysis. "
        features={ [
        {
          fig: 'FIG 1.1',
          icon: <Zap size={80} color="var(--purple)" />,
          title: 'Daily & Hourly Economic Indicators',
          description: 'Daily, monthly, and quarterly releases of key macro indicators - GDP, CPI, PMIs, industrial production - with decades of history retrievable nearly instantly.',
        },
        {
          fig: 'FIG 1.2',
          icon: <Clock size={80} color="var(--green)" />,
          title: 'Deep Historical Archives',
          description: '60+ years of normalized economic data. Build long-term models, backtest macro strategies, and study structural shifts with confidence.',
        },
        {
          fig: 'FIG 1.3',
          icon: <Database size={80} color="var(--red)" />,
          title: 'Central Bank Policy Analytics',
          description: 'Complete central bank meeting calendars, rate decisions, balance sheet data, and forward guidance from the Fed, ECB, BOJ, BOE, and 50+ others.',
        },
        {
          fig: 'FIG 1.4',
          icon: <TrendingDown size={80} color="var(--purple)" />,
          title: 'International Trade & Flows',
          description: 'Bilateral trade balances, import/export volumes, current account statistics, and capital flow data for cross-border analysis.',
        },
        {
          fig: 'FIG 1.5',
          icon: <Activity size={80} color="var(--green)" />,
          title: 'PMI & Sentiment Surveys',
          description: 'Forward-looking signals from PMI surveys, consumer confidence indices, and policy uncertainty trackers. Integrated directly into your models.',
        },
        {
          fig: 'FIG 1.6',
          icon: <Globe size={80} color="var(--red)" />,
          title: 'Global Coverage',
          description: '195+ countries and regions. Seamless access to developed, emerging, and frontier economies - all from a single, unified economic data API.',
        },
      ]}/>



      <Suspense fallback={null}>
      <GlobeComp
        eyebrow="Global Macro Platform"
        headingPlain="Macroeconomic data"
        headingEm="from 195+ countries."
        body="Institutional-grade macroeconomic data API fed by 2M+ economic indicators from over 500 primary sources - central banks, statistical agencies, and international organisations. Clean, calendar-aligned, and ready for production."
        stats={[
          { v: "195+",    l: "Countries"     },
          { v: "2M+",     l: "Indicators"    },
          { v: "500+",    l: "Sources"       },
          { v: "60+ yr",  l: "History"       },
        ]}

        markers={[
          { id: "fed",        location: [38.89, -77.04],   label: "Federal Reserve" },
          { id: "ecb",        location: [50.11, 8.68],     label: "European Central Bank" },
          { id: "boj",        location: [35.68, 139.76],   label: "Bank of Japan" },
          { id: "boe",        location: [51.51, -0.09],    label: "Bank of England" },
          { id: "pbc",        location: [39.91, 116.40],   label: "People's Bank of China" },

          { id: "boc",        location: [45.42, -75.69],   label: "Bank of Canada" },
          { id: "rba",        location: [-35.28, 149.13],  label: "Reserve Bank of Australia" },
          { id: "rbnz",       location: [-41.29, 174.78],  label: "Reserve Bank of New Zealand" },
          { id: "snb",        location: [46.95, 7.45],     label: "Swiss National Bank" },
          { id: "riksbank",   location: [59.33, 18.07],    label: "Swedish Riksbank" },
          { id: "norges",     location: [59.91, 10.75],    label: "Norges Bank" },
          { id: "bcb",        location: [-15.79, -47.88],  label: "Central Bank of Brazil" },
          { id: "banxico",    location: [19.43, -99.13],   label: "Bank of Mexico" },
          { id: "bofa",       location: [52.37, 4.90],     label: "Central Bank of Netherlands" },
          { id: "bof",        location: [37.57, 126.98],   label: "Bank of Korea" },
          { id: "rbi",        location: [18.94, 72.83],    label: "Reserve Bank of India" },
          { id: "cbr",        location: [55.75, 37.62],    label: "Central Bank of Russia" },
          { id: "sarb",       location: [-25.75, 28.19],   label: "South African Reserve Bank" },
          { id: "cbt",        location: [39.93, 32.86],    label: "Central Bank of Turkey" },

          { id: "imf",        location: [38.90, -77.04],   label: "International Monetary Fund" },
          { id: "worldbank",  location: [38.90, -77.04],   label: "World Bank" },
          { id: "oecd",       location: [48.85, 2.35],     label: "OECD" },
          { id: "bis",        location: [47.55, 7.59],     label: "Bank for International Settlements" },
          { id: "wto",        location: [46.23, 6.13],     label: "World Trade Organization" },
          { id: "un",         location: [40.75, -73.97],   label: "United Nations Economic Affairs" },

          { id: "bea",        location: [38.89, -77.02],   label: "US Bureau of Economic Analysis" },
          { id: "bls",        location: [38.88, -77.03],   label: "US Bureau of Labor Statistics" },
          { id: "census",     location: [38.84, -77.08],   label: "US Census Bureau" },
          { id: "treasury",   location: [38.90, -77.04],   label: "US Treasury" },
          { id: "cbo",        location: [38.90, -77.01],   label: "Congressional Budget Office" },

          { id: "statcan",    location: [45.42, -75.70],   label: "Statistics Canada" },
          { id: "eurostat",   location: [50.84, 4.37],     label: "Eurostat" },
          { id: "destatis",   location: [52.52, 13.40],    label: "Germany Statistics Office" },
          { id: "ons",        location: [51.50, -0.12],    label: "UK Office for National Statistics" },
          { id: "insee",      location: [48.85, 2.34],     label: "France INSEE" },
          { id: "istat",      location: [41.90, 12.48],    label: "Italy Statistics Institute" },
          { id: "ine",        location: [40.42, -3.70],    label: "Spain Statistics Institute" },

          { id: "asean",      location: [1.28, 103.85],    label: "ASEAN Economic Community" },
          { id: "mercosur",   location: [-34.60, -58.38],  label: "Mercosur" },
          { id: "eu",         location: [50.85, 4.35],     label: "European Union Commission" },
          { id: "nafta",      location: [45.50, -73.56],   label: "North American Trade Region" },

          { id: "shanghai",   location: [31.23, 121.47],   label: "Shanghai Economic Hub" },
          { id: "hongkong",   location: [22.32, 114.17],   label: "Hong Kong Monetary Authority" },
          { id: "singapore",  location: [1.35, 103.82],    label: "Monetary Authority of Singapore" },
          { id: "taiwan",     location: [25.03, 121.56],   label: "Taiwan Central Bank" },
          { id: "jakarta",    location: [-6.20, 106.85],   label: "Indonesia Economic Hub" },
          { id: "manila",     location: [14.60, 120.98],   label: "Philippines Central Bank" },
          { id: "bangkok",    location: [13.75, 100.50],   label: "Thailand Economic Hub" },
          { id: "kuala",      location: [3.14, 101.69],    label: "Malaysia Economic Hub" },

          { id: "riyadh",     location: [24.71, 46.67],    label: "Saudi Economic Authority" },
          { id: "dubai",      location: [25.20, 55.27],    label: "Dubai Financial Center" },
          { id: "telaviv",    location: [32.08, 34.78],    label: "Israel Central Bank" },
          { id: "cairo",      location: [30.04, 31.24],    label: "Egypt Economic Hub" },
          { id: "lagos",      location: [6.52, 3.37],      label: "Nigeria Economic Hub" },

          { id: "mexico",     location: [19.43, -99.13],   label: "Mexico Economic Hub" },
          { id: "toronto",    location: [43.65, -79.38],   label: "Toronto Financial Center" },
          { id: "nyfed",      location: [40.71, -74.01],   label: "Federal Reserve Bank of New York" },
          { id: "chicago",    location: [41.88, -87.63],   label: "Chicago Economic Center" },
          { id: "sfed",       location: [37.79, -122.40],  label: "San Francisco Fed" },
          { id: "bostonfed",  location: [42.36, -71.06],   label: "Boston Fed" }
        ]}
      />

      <DataGrid
        title="Macroeconomic Data API: The Global Economic Dashboard"
        subtitle="Track, model, and forecast with the world's most comprehensive macroeconomic data. Clean, normalized, and aligned economic indicators from 195+ countries - from GDP and inflation to employment and PMIs."
        bg={databg}
        stats={[
          { label: 'Economic Indicators', value: '2M+' },
          { label: 'Countries & Regions', value: '195+' },
          { label: 'Data History', value: '60+ years' },
        ]}
      />
      <CardList
        title="Model the Global Economy"
        vid={'https://cdn.studio14a.com/axion/products/cards/econ.mp4'}
        points={[
          {
            "title": "Normalized Global Indicators",
            "details": "Access a unified time-series of key economic indicators (CPI, GDP, Unemployment, Trade) across nations, meticulously cleaned, currency-adjusted, and calendar-aligned for direct cross-country comparison.",
            "cta": "Explore economic indicators",
            "url": "/docs"
          },
          {
            "title": "Sentiment & Survey History",
            "details": "Incorporate forward-looking signals from PMI surveys, consumer confidence indices, central bank communications sentiment, and policy uncertainty trackers.",
            "cta": "View survey data",
            "url": "/docs"
          },
          {
            "title": "Policy & Interest Rate Analytics",
            "details": "Model monetary and fiscal policy paths with complete central bank meeting calendars, rate decisions, balance sheet data, and government yield curves across maturities.",
            "cta": "Analyze policy data",
            "url": "/docs"
          }
        ]}
      />

      <Responses
        heading="Macroeconomic Data, Decoded"
        subheading="Tap into 60+ years of economic indicators, upcoming calendar events, and corporate financial data - all from one unified macroeconomic data API built for long-horizon research and backtesting."
        ticker="AAPL"
        tabs={[
    {
      title: 'Economic Calendar',
      description: 'Track upcoming economic releases, central bank meetings, holidays, and market-moving announcements across the globe - filterable by date range, country, currency, and importance level.',
      data: [
        {
          id: "366961",
          title: "New Year's Day",
          country: "AR",
          indicator: "Holidays",
          ticker: null,
          comment: null,
          category: "gov",
          period: "",
          referenceDate: null,
          source: "",
          sourceUrl: "",
          actual: null,
          previous: null,
          forecast: null,
          actualRaw: null,
          previousRaw: null,
          forecastRaw: null,
          currency: "ARS",
          importance: -1,
          date: "2025-01-01T00:00:00.000Z"
        },
        {
          id: "367102",
          title: "Non Farm Payrolls",
          country: "US",
          indicator: "Employment",
          ticker: null,
          comment: null,
          category: "labor",
          period: "Jan",
          referenceDate: "2026-01-01",
          source: "Bureau of Labor Statistics",
          sourceUrl: "https://www.bls.gov",
          actual: "256K",
          previous: "212K",
          forecast: "165K",
          actualRaw: 256000,
          previousRaw: 212000,
          forecastRaw: 165000,
          currency: "USD",
          importance: 3,
          date: "2026-02-06T13:30:00.000Z"
        }
      ],
    },
    {
      title: 'Natural Language Find',
      description: 'Search economic datasets using plain English. Just describe what you need like "semiconductor spending" or "inflation expectations"',
      data: [
        {
          id: "IPG3344S",
          realtimeStart: "2026-07-10",
          realtimeEnd: "2026-07-10",
          title: "Industrial Production: Manufacturing: Durable Goods: Semiconductor and Other Electronic Component (NAICS = 3344)",
          observationStart: "1972-01-01",
          observationEnd: "2026-05-01",
          frequency: "Monthly",
          frequencyShort: "M",
          units: "Index 2017=100",
          unitsShort: "Index 2017=100",
          seasonalAdjustment: "Seasonally Adjusted",
          seasonalAdjustmentShort: "SA",
          lastUpdated: "2026-06-15 08:25:00-05",
          popularity: "44",
          notes: "explanatory notes (https://www.federalreserve.gov/releases/g17/About.htm) issued by the Board of Governors. For recent updates, see the  announcements (https://www.federalreserve.gov/feeds/g17.html) issued by the Board of Governors."
        },
        {
          id: "IPG3344A",
          realtimeStart: "2026-07-10",
          realtimeEnd: "2026-07-10",
          title: "Industrial Production: Manufacturing: Durable Goods: Semiconductor and Other Electronic Component (NAICS = 3344)",
          observationStart: "1972-01-01",
          observationEnd: "2025-01-01",
          frequency: "Annual",
          frequencyShort: "A",
          units: "Index 2017=100",
          unitsShort: "Index 2017=100",
          seasonalAdjustment: "Not Seasonally Adjusted",
          seasonalAdjustmentShort: "NSA",
          lastUpdated: "2026-06-15 08:25:52-05",
          popularity: "13",
          notes: "explanatory notes (https://www.federalreserve.gov/releases/g17/About.htm) issued by the Board of Governors. For recent updates, see the  announcements (https://www.federalreserve.gov/feeds/g17.html) issued by the Board of Governors."
        }
      ],
    },
    {
      title: 'Series Search',
      description: 'Search thousands of economic time series by keyword - from GDP and inflation to global commodity prices - and retrieve metadata including frequency, units, seasonal adjustment, and coverage period.',
      data: [
        {
          id: "PMAIZMTUSDM",
          realtimeStart: "2025-12-25",
          realtimeEnd: "2025-12-25",
          title: "Global price of Corn",
          observationStart: "1990-01-01",
          observationEnd: "2025-06-01",
          frequency: "Monthly",
          frequencyShort: "M",
          units: "U.S. Dollars per Metric Ton",
          unitsShort: "U.S. $ per Metric Ton",
          seasonalAdjustment: "Not Seasonally Adjusted",
          seasonalAdjustmentShort: "NSA",
          lastUpdated: "2025-07-18 09:37:11-05",
          popularity: "54",
          notes: "Value represents the benchmark prices which are representative of the global market."
        },
        {
          id: "PMAIZMTUSDA",
          realtimeStart: "2025-12-25",
          realtimeEnd: "2025-12-25",
          title: "Global price of Corn",
          observationStart: "1990-01-01",
          observationEnd: "2024-01-01",
          frequency: "Annual",
          frequencyShort: "A",
          units: "U.S. Dollars per Metric Ton",
          unitsShort: "U.S. $ per Metric Ton",
          seasonalAdjustment: "Not Seasonally Adjusted",
          seasonalAdjustmentShort: "NSA",
          lastUpdated: "2025-01-27 13:31:17-06",
          popularity: "24",
          notes: "Value represents the benchmark prices which are representative of the global market."
        }
      ],
    },
    {
      title: 'Historical Dataset',
      description: 'Pull the complete historical time series for any economic indicator by series ID - including full revision history across realtime periods, essential for backtesting and point-in-time analysis.',
      data: [
        {
          realtimeStart: "2015-03-13T00:00:00.000Z",
          date: "1971-01-01T00:00:00.000Z",
          value: 63.4
        },
        {
          realtimeStart: "2015-03-13T00:00:00.000Z",
          date: "1971-02-01T00:00:00.000Z",
          value: 63.6
        },
        {
          realtimeStart: "2015-03-13T00:00:00.000Z",
          date: "1971-03-01T00:00:00.000Z",
          value: 62
        },
        {
          realtimeStart: "2015-03-13T00:00:00.000Z",
          date: "1971-04-01T00:00:00.000Z",
          value: 60.8
        }
      ],
    },
    {
      title: 'Corporate Calendar',
      description: 'Get upcoming earnings dates, earnings call schedules, and dividend calendar information for any ticker - complete with analyst consensus estimates for EPS and revenue.',
      data: {
        earnings: {
          earningsDate: ["2026-01-29T21:00:00.000Z"],
          earningsCallDate: ["2025-10-30T21:00:00.000Z"],
          isEarningsDateEstimate: true,
          earningsAverage: 2.6647,
          earningsLow: 2.51,
          earningsHigh: 2.76,
          revenueAverage: 138253076640,
          revenueLow: 136679500000,
          revenueHigh: 142741000000
        },
        exDividendDate: "2025-11-10T00:00:00.000Z",
        dividendDate: "2025-11-13T00:00:00.000Z"
      },
    },
  ]}
/>

      <DatasetDetails full={true}
        title="Comprehensive Macroeconomic Coverage"
        subtitle="Access daily and historical economic indicators for 195+ countries, sourced directly from central banks, national statistical agencies, and international organisations. Decades of history, retrievable nearly instantly. Powers quantitative macro research, economic forecasting, and backtesting worldwide."
        cta="Start Building"
        ctaLink="/login"
        tiles={[
          {
            img: '9.webp',
            title: 'We cover 195+ countries',
            caption: 'You can access normalized economic data for virtually every country in the world.',
            cn: 'scaled corner',
            tile: <W1/>
          },
          {
            img: '7.webp',
            title: 'We provide 2M+ indicators',
            caption: 'You can analyze everything from GDP and inflation to employment and trade across all sectors.',
            cn: 'scaled',
            tile: <W2/>
          },
          {
            img: '13.webp',
            title: 'We source from 500+ agencies',
            caption: 'You get economic data directly from central banks, national statistical agencies, and international organizations.',
            cn: 'scaled',
            tile: <W3/>
          },
          {
            img: '4.webp',
            title: 'We offer 60+ years of history',
            caption: 'You can backtest macro strategies across decades of economic data for robust analysis.',
            cn: 'scaled',
            tile: <W4/>
          },
          {
            img: '2.webp',
            title: 'We update daily and hourly',
            caption: 'You receive economic indicators after each official release, with decades of history at your fingertips.',
            cn: 'scaled',
            tile: <W5/>
          }
        ]}
      />

      <Outcomes
        headline="Numbers that speak for themselves."
        leftCell={{ stat: '94%', title: '94% data accuracy.', desc: 'Rigorous validation against official releases.', showChart: true }}
        rightCells={[
          { stat: '3X', title: '3x Research Velocity.', desc: 'Go from question to insight in minutes.' },
          { stat: '99.9%', title: '99.9% Platform Uptime.', desc: 'Reliability you can count on.', boldDesc: true },
        ]}

      />

      <ProductFAQ slug="economics" />
      <PricingTable
        // The PricingTable component should be configured to show:
        // Free: 1,000 calls/mo, 1 year history
        // Starter: $39.99/mo annual (50k calls, 25yr history)
        // Pro: $159.99/mo annual (1M calls, unlimited history)
        // Enterprise: custom
        // For now we assume the component reads these from context or props;
        // if it needs explicit props we would add them accordingly.
      />
      <GetStarted />

      <Footer />
      </Suspense>
      </StyledProducts>
    </>
  );
};

export default MacroEconomicsPage;
